import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Col, Form, Input, Modal, Row, Select, Spin, Switch, Table } from "antd";
import { find, get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Button from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import CostCentre from "../../../services/api/CostCentre";
import { paginationDefaults } from "../../../utils/properties";
import { showError, showSuccess } from "../../../utils/utilities";
import PFButton from "../../../components/functional_components/Button/PFButton";

const CostCenters = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirm } = Modal;
  const [formCostCenter] = Form.useForm();
  const management = Form.useWatch("management", formCostCenter);
  const [isFormValid, setIsFormValid] = useState(false);
  const [state, setState] = useState({
    tableContent: [],
    managerDelegateOptions: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
  });

  useEffect(() => {
    dataList();
    getRespCDC();
  }, []);

  useEffect(() => {
    if (state.modal.visible && state.modal.data) {
      formCostCenter.setFieldsValue({
        id: state.modal.data?.id || "",
        name: state.modal.data?.name || "",
        segmentId: state.modal.data?.segment?.id || "",
        segmentName: state.modal.data?.segment?.name || "",
        profitCentreId: state.modal.data?.profitCentre?.id || "",
        profitCentreName: state.modal.data?.profitCentre?.name || "",
        managerDelegate: state.modal.data?.respCdc?.cid || undefined,
        management: state.modal.data?.management ?? true,
      });
    }
  }, [state.modal.data, state.modal.visible]);

  const dataList = async (page = state.pagination.currentPage) => {
    dispatch(setLoading(true));
    const costCenters = await CostCentre.costCentre(`page=${page - 1}&size=${state.pagination.size}`);
    if (!costCenters.error && costCenters.data) {
      costCenters.data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(costCenters, "data", []),
        pagination: {
          currentPage: costCenters.currentPage ? costCenters.currentPage + 1 : paginationDefaults.page,
          size: get(costCenters, "pageSize", paginationDefaults.size),
          totalItems: get(costCenters, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(costCenters, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const getRespCDC = async () => {
    dispatch(setLoading(true));
    const managerDelegateOptions = await CostCentre.getManagerDelegate();
    if (!managerDelegateOptions.error && managerDelegateOptions.data) {
      setState((prevState) => ({
        ...prevState,
        managerDelegateOptions: get(managerDelegateOptions, "data", []),
      }));
    } else {
      showError(get(managerDelegateOptions, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
    formCostCenter.resetFields();
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const costCenter = await CostCentre.getCostCentre(id);
    if (!costCenter.error && costCenter.data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data: costCenter.data } }));
    } else {
      showError(get(costCenter, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await CostCentre.deleteCostCentre(id);
        if (!deleteResponse.error) {
          showSuccess();
          dataList();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("costCenters.table.id"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("costCenters.table.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const getRespCdc = (id) => {
    let name = "";
    if (!!id !== false) {
      let managerSelected = find(state.managerDelegateOptions, function (manager) {
        return manager.cid === id;
      });
      name = `${managerSelected.name} ${managerSelected.surname}`;
    }
    return name;
  };

  const onFinish = async (values) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const respCdc = state.managerDelegateOptions.find((item) => item.cid === values.managerDelegate);
    const data = {
      id: values.id,
      name: values.name,
      management: values.management,
      profitCentre: {
        id: values.profitCentreId,
        name: values.profitCentreName,
      },
      segment: {
        id: values.segmentId,
        name: values.segmentName,
      },
      respCdc:
        respCdc !== undefined
          ? {
              username: respCdc.username,
              cid: respCdc.cid,
              name: respCdc.name,
              surname: respCdc.surname,
            }
          : null,
    };
    const saveResponse = await CostCentre.saveCostCentre(data);
    if (!saveResponse.error) {
      showSuccess();
      closeModal();
      dataList();
    } else {
      showError(get(saveResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const onValuesChange = (changedValues, allValues) => {
    const { id, name, segmentId, segmentName, profitCentreId, profitCentreName } = allValues;
    const isValid = !!id && !!name && !!segmentId && !!segmentName && !!profitCentreId && !!profitCentreName;
    setIsFormValid(isValid);
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(page);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.id
            ? state.modal.data.id
            : t("costCenters.new")
        }
        footer={null}
        onCancel={closeModal}
        key={state.modal.visible ? "open" : "closed"}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}

        <Form form={formCostCenter} layout="vertical" onFinish={onFinish} onValuesChange={onValuesChange}>
          <Row gutter={[48, 24]}>
            <Col span={12}>
              <Form.Item
                label={<strong>{t("costCenters.table.id")}</strong>}
                name="id"
                rules={[
                  {
                    required: true,
                    message: t("general.required"),
                  },
                ]}
              >
                <Input disabled={state.modal.type === "edit" ? true : false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<strong>{t("costCenters.table.name")}</strong>}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("general.required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<strong>{t("costCenters.table.segmentId")}</strong>}
                name="segmentId"
                rules={[
                  {
                    required: true,
                    message: t("general.required"),
                  },
                ]}
              >
                <Input disabled={state.modal.type === "edit" ? true : false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<strong>{t("costCenters.table.segmentName")}</strong>}
                name="segmentName"
                rules={[
                  {
                    required: true,
                    message: t("general.required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<strong>{t("costCenters.table.profitCentreId")}</strong>}
                name="profitCentreId"
                rules={[
                  {
                    required: true,
                    message: t("general.required"),
                  },
                ]}
              >
                <Input disabled={state.modal.type === "edit" ? true : false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<strong>{t("costCenters.table.profitCentreName")}</strong>}
                name="profitCentreName"
                rules={[
                  {
                    required: true,
                    message: t("general.required"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>{t("costCenters.table.managerDelegate")}</strong>} name="managerDelegate">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={state.managerDelegateOptions?.map((data) => ({
                    value: data.cid,
                    label: getRespCdc(data.cid),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>{t("costCenters.table.management")}</strong>} name="management">
                <Switch
                  style={{ backgroundColor: management ? "#4096FF" : "#002654" }}
                  checkedChildren={t("costCenters.management")}
                  unCheckedChildren={t("costCenters.real")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Form.Item className="btn-submit">
              <PFButton type="primary" htmlType="submit" currentIcon={<SaveOutlined />} isDisabled={!isFormValid} />
            </Form.Item>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CostCenters;
